import { useQuery } from "@tanstack/react-query";
import { FilterOption, TickIcon, XCloseIcon } from "assests";
import { Button } from "components/UI/Button";
import CustomDropdownCard from "components/UI/CustomDropdownCard";
import TransparencySlider from "components/UI/TransparencySlider";
import ArticleFilters from "pages/Workspace/Article/ArticleFilters";
import { useEffect, useRef, useState } from "react";
import { RxCross2 } from "react-icons/rx";
import { useSelector } from "react-redux";
import Datepicker from "react-tailwindcss-datepicker";
import { getAuditTypes } from "services/audit.services";
import { getAllSuppliers } from "services/supplier.services";
import { convertDate, filterNonEmptyKeys, setTimeInDateObject } from "utils/helper";
const AuditFilters = ({
  workspaceId,
  activeFilters,
  setActiveFilters,
  setFilteredData,
  setAuditOptions,
  auditOptions,
  setPage
}) => {
  const [isHovered, setIshovered] = useState(false);
  const [certificateType, setCertificateType] = useState([]);
  const filterOptions = [
    "days_until_expiry",
    "audit_no",
    "result_type",
    "partner_name",
    "issue_date",
    "expiry_date",
    "uploaded_by",
    "street",
    "city",
    "country",
  ];
  const [supplierList, setSupplierList] = useState([]);
  const [auditDataList, setAuditDataList] = useState([]);
  const filterRef = useRef(null);
  const childfilterRef = useRef(null);
  const [transparencyRange, setTransparencyRange] = useState([0, 365]);
  const [hoveredId, setHoveredId] = useState({});
  const [mainDropdownVisible, setMainDropdownVisible] = useState(false);
  const [currentFilterDropdown, setCurrentFilterDropdown] = useState(null);
  const [uplodedBy, setUplodedBy] = useState([]);
  const [uniqueCountries, setUniqueCountries] = useState([]);
  const [uniqueCities, setUniqueCities] = useState([]);
  const [uniqueStreets, setUniqueStreets] = useState([]);
  const { user, currentWorkspace } = useSelector((state) => state.authenticationSlice);

  const { data: auditTypeQueryData } = useQuery({
    queryKey: ["getauditType", "getAuditTypeList"],
    queryFn: () => getAuditTypes(),
    enabled: !!workspaceId,
  });

  const { data: suppliersQueryData } = useQuery({
    queryKey: ["suppliersdata", "getSuppliers"],
    queryFn: () => getAllSuppliers({ workspace_id: parseInt(workspaceId) }),
    enabled: !!workspaceId,
  });

  // Toggle Main Dropdown
  const toggleMainDropdown = () => {
    setMainDropdownVisible(!mainDropdownVisible);
    setCurrentFilterDropdown(null);
  };
  useEffect(() => {
    if (suppliersQueryData && suppliersQueryData?.data?.data) {
      const result = suppliersQueryData?.data?.data;
      if (result && result?.length > 0) {
        let modifiedList = result
          ?.filter((item) => !item?.pending)
          .map((item) => ({
            value: item?.workspace_id,
            label: item?.company_name,
          }));
        setSupplierList(modifiedList);
      }
    }
  }, [suppliersQueryData]);

  useEffect(() => {
    if (auditTypeQueryData && auditTypeQueryData?.data?.result_type) {
      let modifiedList = auditTypeQueryData?.data?.result_type?.map((item) => ({
        label: item?.name && item?.name === "Failed" ? "Fail" : item?.name,
        value: item?.name,
      }));
      setCertificateType(modifiedList);
    }
  }, [auditTypeQueryData]);

  useEffect(() => {
    if (auditOptions && auditOptions?.length > 0) {
      let modifiedList = auditOptions?.map((item) => ({
        label: item,
        value: item,
      }));
      setAuditDataList(modifiedList);
      let uploadedByModifiedList = auditOptions
        ?.map((item) => item?.updated_by_user?.name)
        .map((item) => ({
          label: item,
          value: item,
        }));
      setUplodedBy(uploadedByModifiedList);
    }
  }, [auditOptions]);

  const processUniqueData = () => {
    // Extract unique countries
    const countries = [
      ...new Set(
        auditOptions
          .filter((item) => item && item.partner_workspace) // Ensure partner_workspace exists
          .map((item) => item.partner_workspace.country) // Access country through partner_workspace
          .filter((country) => country && country.trim() !== ""),
      ),
    ];
    setUniqueCountries(countries);

    // Extract unique cities
    const cities = [
      ...new Set(
        auditOptions
          .filter((item) => item && item.partner_workspace) // Ensure partner_workspace exists
          .map((item) => item.partner_workspace.region) // Access region through partner_workspace
          .filter((region) => region && region.trim() !== ""),
      ),
    ];
    setUniqueCities(cities);

    // Extract unique streets
    const streets = [
      ...new Set(
        auditOptions
          .filter((item) => item && item.partner_workspace) // Ensure partner_workspace exists
          .map((item) => item.partner_workspace.address) // Access address through partner_workspace
          .filter((street) => street && street.trim() !== ""),
      ),
    ];
    setUniqueStreets(streets);
  };

  useEffect(() => {
    if (auditOptions && auditOptions.length > 0) {
      processUniqueData();
    }
  }, [auditOptions]);

  // Add Filter
  const addFilter = (filterName) => {
    if (!activeFilters[filterName]) {
      setActiveFilters((prev) => ({
        ...prev,
        [filterName]: filterName === "Transparency Score" ? transparencyRange : [],
      }));
    }
  };

  // Remove Filter
  const removeFilter = (filterName) => {
    setActiveFilters((prev) => {
      const updatedFilters = { ...prev };
      delete updatedFilters[filterName];
      return updatedFilters;
    });
    setCurrentFilterDropdown(null);
    if (filterName === "days_until_expiry") {
      setTransparencyRange([0, 365]);
    }
  };

  const addFilterValue = (filterName, value, label) => {
    setActiveFilters((prev) => {
      const updatedValues = prev[filterName] || [];
      if (!updatedValues.some((item) => item.value === value)) {
        updatedValues.push({ value, label });
      }
      // Return the updated filters
      return { ...prev, [filterName]: updatedValues };
    });
  };

  const removeFilterValue = (filterName, value) => {
    setActiveFilters((prev) => {
      const updatedValues = prev[filterName]?.filter((item) => item.value !== value);
      if (!updatedValues || updatedValues.length === 0) {
        const { [filterName]: _, ...rest } = prev; // Destructure to exclude the filterName
        return rest;
      }
      return { ...prev, [filterName]: updatedValues };
    });
  };

  // Handle Transparency Slider Change
  const handleSliderChange = (range) => {
    setTransparencyRange(range);
    setActiveFilters((prev) => ({
      ...prev,
      days_until_expiry: range,
    }));
  };

  useEffect(() => {
    if (activeFilters) {
      const transformedData = {
        ranges: activeFilters.days_until_expiry,
        custom_date_range: activeFilters.issue_date,
        custom_updated_date_range: activeFilters.expiry_date,
        supplier_id: activeFilters.partner_name,
        updated_by_user: activeFilters.uploaded_by,
        result_type: activeFilters.result_type,
        audit_id: activeFilters.audit_no,
        filter_by_street: activeFilters.street,
        filter_by_city: activeFilters.city,
        filter_by_country: activeFilters.country,
      };
      const updatedFilteredData = filterNonEmptyKeys(transformedData);
      setFilteredData(updatedFilteredData);
      if (Object.keys(updatedFilteredData).length !== 0) {
        setPage(1);
      }
    }
  }, [activeFilters]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setMainDropdownVisible(false);
      }
    };

    document.addEventListener("click", handleClickOutside);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (childfilterRef.current && !childfilterRef.current.contains(event.target)) {
        setCurrentFilterDropdown(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [currentFilterDropdown]);

  useEffect(() => {
    if (user && currentWorkspace) {
      const handleKeyDown = (event) => {
        // Check if 'Ctrl + R' is pressed

        if (
          (event.ctrlKey || event.metaKey) &&
          event.shiftKey &&
          (event.key === "Y" || event.key === "y")
        ) {
          event.preventDefault();
          setMainDropdownVisible(true);
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [user, currentWorkspace]);

  return (
    <div
      className={`flex flex-col md:flex-row md:flex-wrap  w-full ${activeFilters && Object.keys(activeFilters).length > 0 && "gap-2"}`}
    >
      <div
        ref={childfilterRef}
        className="flex flex-col md:flex-row md:flex-wrap gap-2 order-2 md:order-1"
      >
        {activeFilters &&
          Object.keys(activeFilters).map((filter) => (
            <div key={filter} className="relative">
              <Button
                variant="secondary"
                className="text-txt-secondary px-3.5 py-2.5 justify-between h-10 lg:h-auto  gap-1 truncate w-full md:w-auto text-sm tracking-wide font-normal  flex"
                onClick={() => {
                  setCurrentFilterDropdown(currentFilterDropdown === filter ? null : filter);
                  setMainDropdownVisible(false);
                }}
              >
                <div>
                  <span className="capitalize">
                    {filter === "audit_no" ? "Audit No." : filter?.replace(/_/g, " ")}
                  </span>
                  :
                  {(filter === "issue_date" || filter === "expiry_date") &&
                  activeFilters[filter] ? (
                    <span className="font-semibold">
                      {" "}
                      {activeFilters[filter]?.startDate && activeFilters[filter]?.endDate
                        ? `${convertDate(activeFilters[filter].startDate)} ~ ${convertDate(activeFilters[filter].endDate)}`
                        : null}
                    </span>
                  ) : activeFilters[filter]?.length > 0 ? (
                    <span className="font-semibold">
                      {" "}
                      {activeFilters[filter].length > 2
                        ? "Multiple"
                        : activeFilters[filter]
                            .map((status) =>
                              typeof status === "object" && status.label ? status.label : status,
                            )
                            .join(", ")}
                    </span>
                  ) : null}
                </div>
                <div>
                  <RxCross2
                    size={20}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFilter(filter);
                    }}
                    className="cursor-pointer"
                  />
                </div>
              </Button>

              {/* Sub-dropdown for Status and certificate_no.*/}
              {currentFilterDropdown === filter && filter === "audit_no" && (
                <CustomDropdownCard>
                  {auditDataList && auditDataList.length > 0 ? (
                    auditDataList.map((article, index) => {
                      const isSelected = activeFilters[filter]?.some(
                        (item) => item.value === article.value.audit_id,
                      );
                      return (
                        <div
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            addFilterValue(filter, article.value.audit_id, article.value.audit_id);
                          }}
                          onMouseEnter={() => setHoveredId(article.value.audit_id)}
                          onMouseLeave={() => setHoveredId(null)}
                          className={`cursor-pointer flex items-center justify-start w-full pl-2 pr-2.5 py-2.5 rounded-md hover:bg-global-white ${
                            isSelected ? "bg-global-white" : ""
                          }`}
                        >
                          <span className="text-base font-medium text-global-title truncate">
                            {article.label.audit_id}
                          </span>
                          {isSelected && (
                            <span className="ml-auto">
                              {hoveredId === article.value.audit_id ? (
                                <XCloseIcon
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFilterValue(filter, article.value.audit_id);
                                  }}
                                  strokeColor={"var(--color-login-text-title)"}
                                  width={20}
                                  height={20}
                                />
                              ) : (
                                <TickIcon width={20} height={20} />
                              )}
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-base px-6 py-5 font-normal text-center text-global-gray-brand">
                      No data found
                    </p>
                  )}
                </CustomDropdownCard>
              )}
              {/* Sub-dropdown for Transparency Score */}
              {currentFilterDropdown === filter && filter === "days_until_expiry" && (
                <CustomDropdownCard className="w-[292px] md:w-[320px]">
                  <TransparencySlider
                    setTransparencyRange={setTransparencyRange}
                    transparencyRange={transparencyRange}
                    onChange={handleSliderChange}
                    maxValue={365}
                    minValue={0}
                  />
                </CustomDropdownCard>
              )}
              {currentFilterDropdown === filter && filter === "result_type" && (
                <CustomDropdownCard>
                  {certificateType && certificateType.length > 0 ? (
                    certificateType.map((status, index) => {
                      const isSelected = activeFilters[filter]?.some(
                        (item) => item.value === status.value,
                      );
                      return (
                        <div
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            addFilterValue(filter, status.value, status.label);
                          }}
                          onMouseEnter={() => setHoveredId(status.value)}
                          onMouseLeave={() => setHoveredId(null)}
                          className={`cursor-pointer flex items-center justify-start w-full pl-2 pr-2.5 py-2.5 rounded-md hover:bg-global-white ${
                            isSelected ? "bg-global-white" : ""
                          }`}
                        >
                          <span className="text-base font-medium text-global-title truncate">
                            {status.label}
                          </span>
                          {isSelected && (
                            <span className="ml-auto">
                              {hoveredId === status.value ? (
                                <XCloseIcon
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFilterValue(filter, status.value);
                                  }}
                                  strokeColor={"var(--color-login-text-title)"}
                                  width={20}
                                  height={20}
                                />
                              ) : (
                                <TickIcon width={20} height={20} />
                              )}
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-base px-6 py-5 font-normal text-center text-global-gray-brand">
                      No data found
                    </p>
                  )}
                </CustomDropdownCard>
              )}
              {currentFilterDropdown === filter && filter === "partner_name" && (
                <CustomDropdownCard>
                  {supplierList && supplierList.length > 0 ? (
                    supplierList.map((status, index) => {
                      const isSelected = activeFilters[filter]?.some(
                        (item) => item.value === status.value,
                      );
                      return (
                        <div
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            addFilterValue(filter, status.value, status.label);
                          }}
                          onMouseEnter={() => setHoveredId(status.value)}
                          onMouseLeave={() => setHoveredId(null)}
                          className={`cursor-pointer flex items-center justify-start w-full pl-2 pr-2.5 py-2.5 rounded-md hover:bg-global-white ${
                            isSelected ? "bg-global-white" : ""
                          }`}
                        >
                          <span className="text-base font-medium text-global-title truncate">
                            {status.label}
                          </span>
                          {isSelected && (
                            <span className="ml-auto">
                              {hoveredId === status.value ? (
                                <XCloseIcon
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFilterValue(filter, status.value);
                                  }}
                                  strokeColor={"var(--color-login-text-title)"}
                                  width={20}
                                  height={20}
                                />
                              ) : (
                                <TickIcon width={20} height={20} />
                              )}
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-base px-6 py-5 font-normal text-center text-global-gray-brand">
                      No data found
                    </p>
                  )}
                </CustomDropdownCard>
              )}
              {currentFilterDropdown === filter &&
                (filter === "issue_date" || filter === "expiry_date") && (
                  <div>
                    {activeFilters[filter] && (
                      <div className="absolute z-50 py-2 rounded-lg focus:outline-none bg-global-theme ">
                        <div
                          className="rounded-lg p-6 bg-btn-surface-secondary max-h-[320px] w-[320px] border border-global-divider-soft "
                          style={{
                            boxShadow:
                              "0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)",
                          }}
                        >
                          <div className="grid grid-cols-1 gap-[2px] py-1">
                            <div className="relative">
                              <Datepicker
                                useRange={window.innerWidth > 768 ? true : false}
                                asSingle={false}
                                value={activeFilters[filter]}
                                placeholder={"Select issue date range"}
                                displayFormat="DD-MM-YYYY"
                                inputClassName={`flex justify-between w-full gap-3 px-4 h-[44px]  py-2 items-center text-dropdown-text-data text-base placeholder:text-input-text-placeholder placeholder:text-base placeholder:font-normal self-stretch border border-dropdown-stroke-default bg-dropdown-surface-background focus:shadow-[none] focus:ring-offset-input-surface-background focus:border-input-stroke-active focus:ring-input-stroke-active rounded-lg z-[2]`}
                                containerClassName="certificate-date-picker"
                                toggleClassName="absolute text-icon-fill-input custom-toggle-icon right-0 h-full z-[4]"
                                onChange={(range) => {
                                  setActiveFilters((prev) => ({
                                    ...prev,
                                    [filter]: setTimeInDateObject(range),
                                  }));
                                }}
                                primaryColor={"emerald"}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              {currentFilterDropdown === filter && filter === "street" && (
                <CustomDropdownCard>
                  {uniqueStreets && uniqueStreets.length > 0 ? (
                    uniqueStreets.map((article, index) => {
                      const isSelected = activeFilters[filter]?.some(
                        (item) => item.value === ArticleFilters,
                      );
                      return (
                        <div
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            addFilterValue(filter, article, article);
                          }}
                          onMouseEnter={() => setHoveredId(article)}
                          onMouseLeave={() => setHoveredId(null)}
                          className={`cursor-pointer flex items-center justify-start w-full pl-2 pr-2.5 py-2.5 rounded-md hover:bg-global-white ${
                            isSelected ? "bg-global-white" : ""
                          }`}
                        >
                          <span className="text-base font-medium text-global-title truncate">
                            {article}
                          </span>
                          {isSelected && (
                            <span className="ml-auto">
                              {hoveredId === article ? (
                                <XCloseIcon
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFilterValue(filter, article);
                                  }}
                                  strokeColor={"var(--color-login-text-title)"}
                                  width={20}
                                  height={20}
                                />
                              ) : (
                                <TickIcon width={20} height={20} />
                              )}
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-base px-6 py-5 font-normal text-center text-global-gray-brand">
                      No data found
                    </p>
                  )}
                </CustomDropdownCard>
              )}
              {currentFilterDropdown === filter && filter === "city" && (
                <CustomDropdownCard>
                  {uniqueCities && uniqueCities.length > 0 ? (
                    uniqueCities.map((article, index) => {
                      const isSelected = activeFilters[filter]?.some(
                        (item) => item.value === article,
                      );
                      return (
                        <div
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            addFilterValue(filter, article, article);
                          }}
                          onMouseEnter={() => setHoveredId(article)}
                          onMouseLeave={() => setHoveredId(null)}
                          className={`cursor-pointer flex items-center justify-start w-full pl-2 pr-2.5 py-2.5 rounded-md hover:bg-global-white ${
                            isSelected ? "bg-global-white" : ""
                          }`}
                        >
                          <span className="text-base font-medium text-global-title truncate">
                            {article}
                          </span>
                          {isSelected && (
                            <span className="ml-auto">
                              {hoveredId === article ? (
                                <XCloseIcon
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFilterValue(filter, article);
                                  }}
                                  strokeColor={"var(--color-login-text-title)"}
                                  width={20}
                                  height={20}
                                />
                              ) : (
                                <TickIcon width={20} height={20} />
                              )}
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-base px-6 py-5 font-normal text-center text-global-gray-brand">
                      No data found
                    </p>
                  )}
                </CustomDropdownCard>
              )}
              {currentFilterDropdown === filter && filter === "country" && (
                <CustomDropdownCard>
                  {uniqueCountries && uniqueCountries.length > 0 ? (
                    uniqueCountries.map((article, index) => {
                      const isSelected = activeFilters[filter]?.some(
                        (item) => item.value === article,
                      );
                      return (
                        <div
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            addFilterValue(filter, article, article);
                          }}
                          onMouseEnter={() => setHoveredId(article)}
                          onMouseLeave={() => setHoveredId(null)}
                          className={`cursor-pointer flex items-center justify-start w-full pl-2 pr-2.5 py-2.5 rounded-md hover:bg-global-white ${
                            isSelected ? "bg-global-white" : ""
                          }`}
                        >
                          <span className="text-base font-medium text-global-title truncate">
                            {article}
                          </span>
                          {isSelected && (
                            <span className="ml-auto">
                              {hoveredId === article ? (
                                <XCloseIcon
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeFilterValue(filter, article);
                                  }}
                                  strokeColor={"var(--color-login-text-title)"}
                                  width={20}
                                  height={20}
                                />
                              ) : (
                                <TickIcon width={20} height={20} />
                              )}
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-base px-6 py-5 font-normal text-center text-global-gray-brand">
                      No data found
                    </p>
                  )}
                </CustomDropdownCard>
              )}
              {currentFilterDropdown === filter && filter === "uploaded_by" && (
                <CustomDropdownCard>
                  {uplodedBy && uplodedBy.length > 0 ? (
                    // Use reduce to filter out duplicates based on trimmed labels
                    uplodedBy
                      .reduce((acc, status) => {
                        const trimmedLabel = status.label.trim();
                        // Check if the trimmed label is already in the accumulator
                        if (
                          trimmedLabel &&
                          !acc.some((item) => item.label.trim() === trimmedLabel)
                        ) {
                          acc.push(status);
                        }
                        return acc;
                      }, [])
                      .map((status, index) => {
                        const isSelected = activeFilters[filter]?.some(
                          (item) => item.value === status.value,
                        );
                        return (
                          <div
                            key={index}
                            onClick={(e) => {
                              e.stopPropagation();
                              addFilterValue(filter, status.value, status.label);
                            }}
                            onMouseEnter={() => setHoveredId(status.value)}
                            onMouseLeave={() => setHoveredId(null)}
                            className={`cursor-pointer flex items-center justify-start w-full pl-2 pr-2.5 py-2.5 rounded-md hover:bg-global-white ${
                              isSelected ? "bg-global-white" : ""
                            }`}
                          >
                            <span className="text-base font-medium text-global-title truncate">
                              {status.label}
                            </span>
                            {isSelected && (
                              <span className="ml-auto">
                                {hoveredId === status.value ? (
                                  <XCloseIcon
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeFilterValue(filter, status.value);
                                    }}
                                    strokeColor={"var(--color-login-text-title)"}
                                    width={20}
                                    height={20}
                                  />
                                ) : (
                                  <TickIcon width={20} height={20} />
                                )}
                              </span>
                            )}
                          </div>
                        );
                      })
                  ) : (
                    <p className="text-base px-6 py-5 font-normal text-center text-global-gray-brand">
                      No data found
                    </p>
                  )}
                </CustomDropdownCard>
              )}
            </div>
          ))}
      </div>
      <div className="order-1 md:order-2">
        <div ref={filterRef} className="w-full md:w-auto">
          <Button
            variant="secondary"
            className="text-txt-secondary px-[15.08px] py-2.5 w-full md:w-auto  gap-1.5 text-sm   font-semibold flex h-10 lg:h-auto"
            onMouseEnter={() => setIshovered(true)}
            onMouseLeave={() => setIshovered(false)}
            onClick={toggleMainDropdown}
          >
            <FilterOption
              width={20}
              height={20}
              strokeColor={
                isHovered ? "var(--color-icon-fill-secondary-button)" : "var(--color-txt-secondary)"
              }
            />
            {Object.keys(activeFilters).length > 0 ? (
              "More filters"
            ) : (
              <span className="tracking-[0.01em]">Filters</span>
            )}
          </Button>
          {mainDropdownVisible && (
            <CustomDropdownCard>
              {filterOptions.map((filter, index) => {
                const isSelected = activeFilters[filter];
                return (
                  <div
                    key={index}
                    onClick={() => addFilter(filter)}
                    onMouseEnter={() => setHoveredId(filter)}
                    onMouseLeave={() => setHoveredId(null)}
                    className={`cursor-pointer flex gap-2 flex-wrap items-center justify-start w-full pl-2 pr-2.5 py-2.5 rounded-md hover:bg-global-white ${
                      isSelected ? "bg-global-white" : ""
                    }`}
                  >
                    <span className="text-base font-medium text-global-title truncate capitalize">
                      {filter === "audit_no" ? "Audit No." : filter?.replace(/_/g, " ")}
                    </span>
                    {isSelected && (
                      <span className="ml-auto">
                        {hoveredId === filter ? (
                          <XCloseIcon
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFilter(filter);
                            }}
                            strokeColor={"var(--color-login-text-title)"}
                            width={20}
                            height={20}
                          />
                        ) : (
                          <TickIcon width={20} height={20} />
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
            </CustomDropdownCard>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditFilters;
